import React, { useState, useEffect } from 'react';
import './ChemicalDisclosure.css';
import ChemicalDisclosureTable from './ChemicalDisclosureTable';
import IngredientsService from '../services/IngredientsService';
import { toast } from 'react-toastify';

const ChemicalDisclosure = ({ wellData, waterSource, onIngredientsChange, onComplete }) => {
  const [ingredients, setIngredients] = useState([]);
  const [isValid, setIsValid] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(null);


  // Process chemicals from wellData on component mount
  useEffect(() => {
    console.log('ChemicalDisclosure mount:', {
      customer_id: wellData?.customer_id,
      customer: wellData?.customer
    });
    
    const loadIngredients = async () => {
      if (!wellData) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Always run the lookup first if we have a customer name
        let customerId = null;
        
        if (wellData.customer) {
          console.log(`Looking up customer ID for: ${wellData.customer}`);
          console.log(`wellData: ${wellData}`);
          customerId = await IngredientsService.findCustomerIdByName(wellData.customer);
          console.log(`Found customer ID from lookup: ${customerId}`);
        }
        
        // If lookup failed, fall back to the existing customer_id
        if (!customerId) {
          customerId = wellData.customer_id;
          console.log(`Using existing customer_id: ${customerId}`);
        }
        
        // If no customer ID found, log a warning but don't set a hardcoded value
        if (!customerId) {
          console.log('No customer ID found from lookup. Proceeding without a customer ID.');
        }
        
        // Store the found customer_id in state for later use
        setCustomerId(customerId);
        
        // Ensure customer_id is set in wellData for third-party chemical mappings
        const wellDataWithCustomerId = {
          ...wellData,
          customer_id: customerId
        };
        
        console.log('wellDataWithCustomerId:', wellDataWithCustomerId);
        console.log(`Customer ID being passed to processChemicals: ${customerId}`);
        
        // Process chemicals from wellData
        const processedIngredients = await IngredientsService.processChemicals(wellDataWithCustomerId);
        setIngredients(processedIngredients);
        console.log(processedIngredients)
        
        // Validate the processed ingredients
        validateIngredients(processedIngredients);
      } catch (error) {
        console.error('Error processing chemicals:', error);
        setError('Failed to process chemicals. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadIngredients();
  }, [wellData]); // Re-run when wellData changes

  // Validate ingredients whenever they change
  useEffect(() => {
    if (ingredients && ingredients.length > 0) {
      validateIngredients(ingredients);
      
      // Update parent component if needed
      if (onIngredientsChange) {
        onIngredientsChange(ingredients);
      }
    }
  }, [ingredients, onIngredientsChange]);

  // Validate ingredients and update isValid state
  const validateIngredients = (ingredientsToValidate) => {
    if (!ingredientsToValidate || ingredientsToValidate.length === 0) {
      setIsValid(false);
      return;
    }
    
    // Calculate total percentage - this should now sum to 1.0 (100%)
    // since percentInHFJob is calculated as massIngredient / totalMass
    const totalPercentage = ingredientsToValidate.reduce((sum, ingredient) => {
      return sum + (ingredient.percentInHFJob || 0);
    }, 0);
    
    // Check if total is within acceptable range (0.97 - 1.03)
    const isPercentageValid = totalPercentage >= 0.97 && totalPercentage <= 1.03;
    
    // Check if all required fields are filled
    const hasRequiredFields = ingredientsToValidate.every(ingredient => 
      ingredient.ingredientName && 
      (ingredient.isNonMsds || 
        (ingredient.tradeName && ingredient.supplier && ingredient.purpose))
    );
    
    // Check if all ingredients have a valid mass
    const hasValidMass = ingredientsToValidate.every(ingredient => 
      parseFloat(ingredient.massIngredient) > 0
    );
    
    // Check if all proprietary chemicals have vendor associations
    const hasVendorAssociations = ingredientsToValidate.every(ingredient => 
      !(ingredient.casNumber && 
        (ingredient.casNumber.toLowerCase().includes('proprietary') || 
         ingredient.casNumber.toLowerCase() === 'n/a')) || 
      ingredient.supplier
    );
    
    // Update validity state
    setIsValid(isPercentageValid && hasRequiredFields && hasValidMass && 
               hasVendorAssociations && ingredientsToValidate.length > 0);
  };

  // Handle completion - call onComplete callback to proceed to XML generation
  const handleComplete = () => {
    if (!isValid) {
      toast.error('Please correct all errors before proceeding');
      return;
    }
    
    try {
      setIsGenerating(true);
      
      // Calculate total mass for wellData - sum of all ingredient masses
      const totalMass = ingredients.reduce((sum, ingredient) => {
        return sum + (parseFloat(ingredient.massIngredient) || 0);
      }, 0);
      
      // Prepare well data for XML generation
      const updatedWellData = {
        ...wellData,
        customer_id: customerId,
        totalMass: totalMass,
        waterSource: waterSource,
        // Ensure all required fields are present
        wellName: wellData.wellName || wellData.name,
        apiNumber: wellData.apiNumber || wellData.api,
        startDate: wellData.startDate || wellData.startTime,
        endDate: wellData.endDate || wellData.endTime,
        state: wellData.state,
        county: wellData.county,
        tvd: wellData.tvd,
        totalClean: wellData.totalCleanVolume || wellData.clean,
        longitude: wellData.longitude,
        latitude: wellData.latitude,
        operatorNumber: wellData.operatorNumber || wellData.operator,
        federalWell: wellData.federalWell || 'No',
        indianWell: wellData.indianWell || 'No',
        datum: wellData.datum || 'NAD83'
      };
      
      // Call onComplete callback with updated data
      // We don't generate XML here - that happens in the XmlGenerator component
      if (typeof onComplete === 'function') {
        onComplete(updatedWellData, ingredients);
      }
    } catch (error) {
      console.error('Error preparing data for XML generation:', error);
      toast.error(`Failed to prepare data: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="chemical-disclosure">
      <div className="chemical-disclosure-header">
        <h2>Chemical Disclosure</h2>
        <p>Review and edit chemical ingredients for FracFocus disclosure.</p>
        
        {wellData && (
          <div className="well-summary">
            <h3>{wellData.wellName || wellData.name}</h3>
            <div className="well-details">
              <p><strong>API:</strong> {wellData.apiNumber || wellData.api || 'N/A'}</p>
              <p><strong>TVD:</strong> {wellData.tvd ? Math.round(wellData.tvd) : 'N/A'} {wellData.tvd ? 'ft' : ''}</p>
              <p><strong>Clean Volume:</strong> {wellData.totalCleanVolume || wellData.clean ? Math.round(wellData.totalCleanVolume || wellData.clean) : 'N/A'} {wellData.totalCleanVolume || wellData.clean ? 'gal' : ''}</p>
              {waterSource && (
                <p>
                  <strong>Water Source:</strong>{' '}
                  {typeof waterSource === 'object' 
                    ? (waterSource.type || waterSource.source || JSON.stringify(waterSource).replace(/[{}"]/g, ''))
                    : waterSource}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {isLoading ? (
        <div className="loading-message">Loading ingredients data...</div>
      ) : (
        <>
          {/* The ChemicalDisclosureTable component handles all ingredient management */}
          <ChemicalDisclosureTable 
            ingredients={ingredients}
            setIngredients={setIngredients}
            wellData={wellData}
            totalClean={wellData?.totalCleanVolume || wellData?.clean || 0}
          />
          
          {!isValid && (
            <div className="validation-warning">
              <p>Please correct the following before proceeding to XML generation:</p>
              <ul>
                <li>Ensure all ingredients have a name</li>
                <li>MSDS components must have trade name, supplier, and purpose</li>
                <li>Total percentage should be between 97% and 103%</li>
                <li>All ingredients must have a valid mass (volume × 8.345 × specific gravity)</li>
                <li>All CAS numbers should be valid or marked as Proprietary/N/A</li>
                <li>All proprietary chemicals must have a supplier/vendor association</li>
              </ul>
            </div>
          )}
          
          <div className="disclosure-actions">
            <button 
              className="complete-button"
              onClick={handleComplete}
              disabled={!isValid || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Proceed to XML Generation'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChemicalDisclosure;
